import React, { useMemo, useState, useRef, useEffect, useContext } from "react";
import { Platform, Animated } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { RouteProp, useNavigation } from "@react-navigation/native";
import { useRoute } from "@react-navigation/native";
import { useAuth } from "../../../hooks/useAuth";
import { useVisitSession } from "../../../contexts/VisitSessionContext";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import {
  useGetEnrolledCoursesBySchoolQuery,
  useGetUserPermissionsQuery,
  useMeQuery,
  useSchoolsQuery,
} from "../../../graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import useWebSocket from "../../../hooks/useWebSocket";
import handleOpenLink from "../../../utils/handleOpenLink";
import { TodosIcon, EducationIcon, CareerIcon, HealthIcon } from "../../../components/svgs/menuIcons";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import TopMenu from "../../../components/navigation/TopMenu/TopMenu";
import { useChatBar } from "../../../contexts/ChatBarContext";

import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import NotificationsSlideIn from "../../../components/navigation/NotificationSlideIn/NotificationSlideIn";
import SlideInMenuContainer from "../SlideInMenuContainer/SlideInMenuContainer";
import { CourseDetailsContext } from "../../../contexts/CourseDetailsContext";
import { RolePermissions } from "../../../constants/rolesAndPermissions";

type MenuItem = {
  display: string;
  name: keyof RootStackParamList;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  externalURL: string | undefined;
  index: number;
  requiredPermission?: string;
};

const ITEMS_PER_PAGE = 30;

function TopMenuContainer() {
  const appConfig = useAppConfig();
  const { sendMessage } = useWebSocket();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList>>();

  const { setShowChat } = useChatBar();

  //Notifcation based on route name.

  const { logout } = useAuth();
  const { endSession } = useVisitSession();
  const { dispatch } = useAppState();

  // const { data: dailyLimitData } = useDailyLimitQuery();
  const { data: userPermissionsData } = useGetUserPermissionsQuery();
  const { data: meData } = useMeQuery();
  const { data: schoolsData, loading: schoolsLoading } = useSchoolsQuery();

  const { data: courses, loading: coursesLoading } = useGetEnrolledCoursesBySchoolQuery({
    variables: {
      schoolId: schoolsData?.getSchools[0]?.id,
      page: 1,
      pageSize: ITEMS_PER_PAGE,
    },
    skip: !schoolsData || schoolsLoading,
  });

  // Animation setup

  const [progressVisible, setProgressVisible] = useState(false);
  const slideAnim = useRef(new Animated.Value(270)).current;

  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const notificationsSlideAnim = useRef(new Animated.Value(360)).current;

  const { isVisible: isCourseDetailsVisible, setIsVisible: setIsCourseDetailsVisible } =
    useContext(CourseDetailsContext);

  const { scope, isLTI, showMiniLogo, key } = appConfig;

  const DomainConfigMenuItems: MenuItem[] = [
    { display: "Dashboard", name: "Home", icon: CareerIcon, externalURL: undefined, index: 0 },
    { display: "Course Catalogue", name: "Course Catalogue", icon: EducationIcon, externalURL: undefined, index: 1 },
    { display: "Courses", name: "Custom Course Student", icon: EducationIcon, externalURL: undefined, index: 2 },
    { display: "Productivity", name: "Productivity", icon: TodosIcon, externalURL: undefined, index: 3 },
    { display: "Portfolio", name: "Portfolio", icon: CareerIcon, externalURL: undefined, index: 4 },
    // Until we can make coaching it's own page
    // @ts-expect-error - This is a valid menu item
    { display: "AI Assistant", name: "Coaching", icon: CareerIcon, externalURL: undefined, index: 5 },
    { display: "Analytics", name: "Analytics", icon: CareerIcon, externalURL: undefined, index: 6 },
    { display: "Career", name: "Career", icon: CareerIcon, externalURL: undefined, index: 7 },
    { display: "Health", name: "Health", icon: HealthIcon, externalURL: undefined, index: 8 },
    { display: "Insights", name: "Insights", icon: CareerIcon, externalURL: undefined, index: 9 },
    {
      display: "Grading Center",
      name: "Grading Center",
      icon: CareerIcon,
      externalURL: undefined,
      index: 10,
      requiredPermission: RolePermissions.courseEdit,
    },
  ];

  const menuItems = useMemo(() => {
    const filtered = DomainConfigMenuItems.filter((item) => {
      let permissionCheck = true;
      if (item.requiredPermission) {
        permissionCheck = userPermissionsData?.getUserPermissions.includes(item.requiredPermission);
      }
      return scope.includes(item.name) && permissionCheck;
    }).sort((a, b) => a.index - b.index);

    const homeItem = DomainConfigMenuItems.find((item) => item.name === "Home");
    if (homeItem && !filtered.some((item) => item.name === "Home")) {
      filtered.unshift(homeItem);
    }

    return filtered;
  }, [scope, userPermissionsData]);

  useEffect(() => {
    if (isCourseDetailsVisible) {
      setNotificationsVisible(false);
      setProgressVisible(false);
    }
  }, [isCourseDetailsVisible]);

  useEffect(() => {
    if (notificationsVisible) {
      Animated.timing(notificationsSlideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(notificationsSlideAnim, {
        toValue: 360,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [notificationsVisible, notificationsSlideAnim]);

  useEffect(() => {
    if (progressVisible) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 270,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [progressVisible, slideAnim]);

  const handleItemPress = (item) => {
    if (item.name === "Coaching") {
      navigation.navigate("Custom School Student");
      setShowChat(true);
    } else {
      setShowChat(false);
    }
    if (item.externalURL) {
      let newWindow;
      if (Platform.OS === "web") {
        newWindow = window.open("", "_blank");
      }
      handleOpenLink(item.externalURL, newWindow);
    } else if (item.name === "Custom Course Student" || item.name === "Activities") {
      handleCourseworkNavigation();
    } else {
      navigation.navigate(item.name);
      handleNavigate();
    }
  };

  const handleNotificationsToggle = () => {
    setNotificationsVisible(!notificationsVisible);
  };

  const handleCourseworkNavigation = () => {
    if (schoolsData?.getSchools && schoolsData.getSchools.length === 1) {
      navigation.navigate("Custom School Student");
    } else {
      navigation.navigate("Coursework");
    }
    handleNavigate();
  };

  const handleProgressToggle = () => {
    setProgressVisible(!progressVisible);
  };

  const handleSettingsPress = () => {
    navigation.navigate("Settings");
    handleNavigate();
  };

  const handleLogoutPress = async () => {
    await endSession();
    logout();
  };

  const handleNavigate = async () => {
    const token = await AsyncStorage.getItem("token");
    sendMessage({
      meta: {
        token: token,
        abort: true,
      },
    });

    dispatch({
      type: actions.SET_META,
      payload: {
        isMainMenuExpanded: false,
        isLearnMenuOpen: false,
        isCustomCourseworkMenuOpen: false,
      },
    });

    setNotificationsVisible(false);
    setProgressVisible(false);
    setIsCourseDetailsVisible(false);
  };

  const showLearnSectionOrName = (name) => {
    if (name === "Tracks" || name === "Categories" || name === "Track Overview") return "Learn";
    if (name === "School") return "Coursework";
    if (name === "Custom School Student") return "Custom Course Student";
    return name;
  };

  return (
    <>
      {progressVisible && !coursesLoading && (
        <SlideInMenuContainer
          courses={courses?.getEnrolledCoursesBySchool.data?.courses || []}
          courseIds={courses?.getEnrolledCoursesBySchool.data?.courses.map((course) => course.id) || []}
          visible={progressVisible}
          slideAnim={slideAnim}
          onClose={() => setProgressVisible(false)}
          onSettingsPress={handleSettingsPress}
        />
      )}

      <TopMenu
        menuItems={menuItems}
        selectedItem={showLearnSectionOrName(route.name)}
        currentDomain={key}
        showMiniLogo={showMiniLogo}
        userInitial={meData?.me?.name?.charAt(0)}
        isLTI={isLTI}
        onItemPress={handleItemPress}
        onSettingsPress={handleProgressToggle}
        onLogoutPress={handleLogoutPress}
        onNotifcationsPress={handleNotificationsToggle}
      />
      <NotificationsSlideIn
        visible={notificationsVisible}
        slideAnim={notificationsSlideAnim}
        onClose={() => setNotificationsVisible(false)}
        notifications={[]} // Pass your notifications array here when you have them
      />
    </>
  );
}

export default TopMenuContainer;
